import {announce} from '@github-ui/aria-live'
import {clsx} from 'clsx'
import {
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
  type Dispatch,
  type SetStateAction,
} from 'react'
import {copilotLocalStorage} from '@github-ui/copilot-chat-helpers/copilot-local-storage'
import {ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE} from '@github-ui/copilot-chat-helpers/copilot-onboarding'
import {dismissNotice} from '@github-ui/copilot-shared-ui/components/onboarding/notice-dismissal'
import {useLastUsedRepository} from '@github-ui/copilot-shared-ui/repos/use-last-used-repository'
import type {TaskComposerTargetSelection} from '@github-ui/copilot-shared-ui/utils/task-composer-target-selection'
import {EnvironmentStrip} from '../task/environment-strip/EnvironmentStrip'
import {isEnvironmentStripTourEnabled} from '../task/environment-strip/EnvironmentStripTour'
import type {ChatOwnerControl} from '../task/environment-strip/chat-owner-control'
import {
  CLOUD_ENVIRONMENT_OPTION,
  SANDBOX_ENVIRONMENT_OPTION,
  type EnvironmentOption,
} from '../task/environment-strip/environment-option'
import {
  canPrepareTaskComposerChat,
  getDraftStorageKeySuffix,
  isOrganizationSandboxEnabled,
  scopeLogin,
  type AvailableOrganization,
  type CreateTaskReference,
  type TaskComposerScope,
} from '../task/shared'
import {
  isSandboxComposedDraft,
  resolveEnvironmentOptions,
  resolvePersistedEnvironmentOption,
  useRepositorySandboxEnabled,
  useEnvironmentSelection,
} from '../task/use-environment-selection'
import {useTaskComposerScope} from '../task/use-task-composer-scope'
import {useTaskComposerTargetState} from '../task/use-task-composer'
import type {Repository} from '@github-ui/current-repository'
import type {AhpProvisioningTarget} from '@github-ui/ahp-session-client/contracts'
import {useSearchParams} from '@github-ui/use-navigate'
import {copilotFeatureFlags} from '@github-ui/copilot-feature-flags'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import {clearPendingSsoChatTarget, setPendingSsoChatTarget} from '../task/pending-sso-target'

import {NewSessionComposerController} from './NewSessionComposerController'
import {
  buildChatOwnerPresentation,
  getPendingSsoChatTargetServerSnapshot,
  getPendingSsoChatTargetSnapshot,
  isOwnerSplitPickerActive,
  resolvePendingSsoChatReturn,
  subscribeToPendingSsoChatTarget,
} from './owner-split-picker'
import {useSharedSessionDraft} from './use-shared-session-draft'
import type {NewSessionComposerProps, NewSessionTarget} from '../props'
import styles from './NewSessionComposer.module.css'
import responsiveStyles from '../shared/responsive-composer.module.css'

const SANDBOX_ONLY_ENVIRONMENT_OPTIONS: EnvironmentOption[] = [SANDBOX_ENVIRONMENT_OPTION]

interface InitialTaskContext {
  repository?: Repository
  repositoryNameWithOwner?: string
  reference?: CreateTaskReference
  branch?: string
}

interface BranchState {
  selectedBranch: string
  setSelectedBranch: Dispatch<SetStateAction<string>>
}

function getInitialTaskContext(target: NewSessionTarget | undefined): InitialTaskContext | undefined {
  if (target?.kind !== 'task') return undefined

  const context: InitialTaskContext = {}
  if (target.reference) context.reference = target.reference
  if (!target.repository) return context

  context.branch = target.repository.branch
  if (target.repository.kind === 'repository') {
    context.repository = target.repository.repository
  } else {
    context.repositoryNameWithOwner = target.repository.nameWithOwner
  }

  return context
}

interface InitialChatScope {
  scope?: TaskComposerScope
  preservedOwner?: string
  preservedOwnerRequiresSso?: boolean
}

function getInitialChatScope(
  target: AhpProvisioningTarget | undefined,
  currentUserLogin: string | undefined,
  availableOrganizations: AvailableOrganization[],
  requireSsoAuthorization: boolean,
): InitialChatScope {
  const owner = target?.kind === 'scope' ? target.owner : target?.kind === 'repository' ? target.scopeOwner : undefined
  if (!owner) return {}
  if (owner.toLowerCase() === currentUserLogin?.toLowerCase()) return {scope: {kind: 'personal'}}

  const organization = availableOrganizations.find(candidate => candidate.login.toLowerCase() === owner.toLowerCase())
  // A denied owner remains ineligible after SSO, so do not preserve it for authorization.
  if (organization && !isOrganizationSandboxEnabled(organization)) return {}
  if (organization?.requiresSso && requireSsoAuthorization) {
    return {preservedOwner: owner, preservedOwnerRequiresSso: true}
  }
  return organization ? {scope: {kind: 'organization', organization}} : {preservedOwner: owner}
}

function getRepositoryNameWithOwner(repository: {ownerLogin: string; name: string} | undefined): string | undefined {
  return repository ? `${repository.ownerLogin}/${repository.name}` : undefined
}

function getInitialRepositoryOwner(initialTask: InitialTaskContext | undefined): string | undefined {
  if (initialTask?.repository) return initialTask.repository.ownerLogin
  return initialTask?.repositoryNameWithOwner?.split('/', 1)[0]
}

function getInitialRepositoryName(
  initialTask: InitialTaskContext | undefined,
  initialTarget: NewSessionTarget | undefined,
  searchParams: URLSearchParams,
  lastUsedRepositoryName: string | undefined,
): string | undefined {
  if (initialTask?.repositoryNameWithOwner) return initialTask.repositoryNameWithOwner
  if (initialTask?.repository) return getRepositoryNameWithOwner(initialTask.repository)
  if (!initialTarget && searchParams.get('repo')) return searchParams.get('repo') ?? undefined
  return lastUsedRepositoryName
}

function getDraftScope(
  initialRepositoryName: string | undefined,
  initialTarget: NewSessionTarget | undefined,
  searchParams: URLSearchParams,
  draftSource: string | undefined,
): string | undefined {
  if (initialTarget?.kind === 'chat') return 'chat'
  if (draftSource === 'agents_panel') return undefined
  return initialRepositoryName ?? searchParams.get('repo') ?? undefined
}

/**
 * The composer for a session that does not exist yet.
 *
 * This layer owns what survives a destination change: the destination, shared
 * draft, environment, scope, attachments, and model selection.
 */
export function NewSessionComposer(props: NewSessionComposerProps) {
  const {controller: host, draftSource, configuration = {}, presentation = {}} = props
  const [mountInitialInput] = useState(() => props.initialInput)
  const [searchParams] = useSearchParams()
  const {
    readOnly = false,
    textAreaRef: textAreaRefFromProps,
    chatInputRef,
    className,
    emptyState,
    onRepositorySelectionChange,
    clearOnSubmit: shouldClearTextOnSubmit = true,
    disableWhileSubmitting = false,
    isNewConversation,
    submitWithModifier = false,
    maxTextareaHeight,
    handleRef,
    disableAutoFocus,
    showEnvironmentStripTour = false,
  } = presentation
  const {
    currentUserLogin,
    supportsManagedSandbox,
    agentQuota,
    mcpPermissionGateEnabled,
    disableSandboxSubmission,
    directTurnTelemetrySurface,
  } = configuration
  const {
    currentUserAvatarUrl,
    availableOrganizations = [],
    lastContributedOrganizationLogin,
    scopePicker,
    scopeOwnerPanel,
    mode,
    onModeChange,
    approvalMode,
    availableApprovalModes,
    onApprovalModeChange,
    modePicker,
  } = configuration
  const initialTarget = mountInitialInput?.target
  const storedSsoOrgForChat = useSyncExternalStore(
    subscribeToPendingSsoChatTarget,
    getPendingSsoChatTargetSnapshot,
    getPendingSsoChatTargetServerSnapshot,
  )
  const [restoredSsoOrgForChat, setRestoredSsoOrgForChat] = useState<string | null>(null)
  const [targetMarkerWrittenHere, setTargetMarkerWrittenHere] = useState(false)
  const pendingSsoOrgForChat = targetMarkerWrittenHere ? null : (restoredSsoOrgForChat ?? storedSsoOrgForChat)
  const pendingSsoChatTarget =
    copilotFeatureFlags.oneCopilotChatOwnerSplitPicker &&
    resolvePendingSsoChatReturn({
      pending: {present: pendingSsoOrgForChat !== null, pendingOrgLogin: pendingSsoOrgForChat},
      organizations: availableOrganizations,
    })
  // Actions submissions cannot carry prepared attachments, so this handoff
  // stays Sandbox-only for its mount instead of silently dropping context.
  const requiresSandboxTaskEnvironment =
    initialTarget?.kind === 'task' && Boolean(mountInitialInput?.prompt?.attachments?.length)
  const initialTask = getInitialTaskContext(initialTarget)
  const initialChatProvisioningTarget = initialTarget?.kind === 'chat' ? initialTarget.provisioningTarget : undefined
  const createTask = host.createTask
  // Actions/Cloud coding-agent tasks are gated on `createTask`; a host without
  // it is never offered the Actions environment (see `resolveEnvironmentOptions`).
  const hasTaskDestination = Boolean(createTask)
  // The repository picker is offered when the host can act on a repository
  // through *either* destination: an Actions task (`createTask`) or a Sandbox
  // task (`createEnvironmentTask`). A host with neither cannot send a repository,
  // so choosing one would move the destination somewhere it cannot send — the
  // picker stays withheld in that case only.
  const hasRepositoryDestination = hasTaskDestination || Boolean(host.createEnvironmentTask)
  // A locked launch keeps the draft on its repository, so the picker that would
  // move it elsewhere — to another repository or to Chat — is withheld too.
  const isRepositoryLocked =
    hasRepositoryDestination &&
    initialTarget?.kind === 'task' &&
    Boolean(initialTarget.lockRepository && initialTarget.repository)
  const hideRepoSelector = !hasRepositoryDestination || isRepositoryLocked
  const chatLaunch = host.chatLaunch
  const [initialChatScope] = useState(() =>
    getInitialChatScope(
      initialChatProvisioningTarget,
      currentUserLogin,
      availableOrganizations,
      chatLaunch?.kind === 'environment',
    ),
  )

  const [lastUsedRepository] = useLastUsedRepository()
  const initialRepositoryName = getInitialRepositoryName(
    initialTask,
    initialTarget,
    searchParams,
    getRepositoryNameWithOwner(lastUsedRepository ?? undefined),
  )
  const hasSpecifiedRepository = Boolean(
    initialTask?.repositoryNameWithOwner || initialTask?.repository || (!initialTarget && searchParams.get('repo')),
  )
  const branchParam = initialTarget || pendingSsoChatTarget ? null : searchParams.get('branch')
  const initialBranch = pendingSsoChatTarget ? undefined : (initialTask?.branch ?? branchParam ?? undefined)
  const draftThreadId = getDraftStorageKeySuffix(
    draftSource,
    getDraftScope(initialRepositoryName, initialTarget, searchParams, draftSource),
  )

  const targetState = useTaskComposerTargetState(
    initialTarget?.kind === 'chat' && supportsManagedSandbox !== false ? {kind: 'chat'} : undefined,
  )
  const {targetSelection, setTargetSelection, textAreaRef} = targetState
  const selectedRepositorySandboxEnabled = useRepositorySandboxEnabled(
    targetSelection.kind === 'repository' ? targetSelection.repository.id : undefined,
  )
  const supportsSelectedRepositorySandbox =
    targetSelection.kind === 'repository'
      ? selectedRepositorySandboxEnabled === true
      : (supportsManagedSandbox ?? false)
  const isRepositorySandboxPolicyPending =
    targetSelection.kind === 'repository' && selectedRepositorySandboxEnabled === undefined
  // The target state's ref stays on the rendered composer: image insertion, the
  // new-chat preset, and the replacement-draft guard all read it, and handing
  // the composer an external ref instead would leave it null and quietly break
  // them. An external ref is mirrored onto the same node rather than replacing it.
  useImperativeHandle<HTMLTextAreaElement | null, HTMLTextAreaElement | null>(
    textAreaRefFromProps ?? null,
    () => textAreaRef.current,
  )

  const draft = useSharedSessionDraft({
    draftThreadId,
    textAreaRef,
    initialPrompt: () =>
      mountInitialInput?.prompt?.text ??
      (searchParams.get('prompt') ||
        copilotLocalStorage.getSavedUserMessageOnError(draftThreadId) ||
        copilotLocalStorage.getSavedMessage(draftThreadId, 'task') ||
        ''),
  })
  const {currentPrompt, setCurrentPrompt} = draft
  useImperativeHandle(chatInputRef ?? null, () => ({setText: setCurrentPrompt}), [setCurrentPrompt])

  const [selectedBranch, setSelectedBranch] = useState(initialBranch ?? '')
  const handledPendingSsoChatTarget = useRef(false)
  const pendingSsoChatFrame = useRef<number | null>(null)
  useEffect(
    () => () => {
      if (pendingSsoChatFrame.current !== null) {
        cancelAnimationFrame(pendingSsoChatFrame.current)
        pendingSsoChatFrame.current = null
      }
    },
    [],
  )
  const branch = useMemo<BranchState>(() => ({selectedBranch, setSelectedBranch}), [selectedBranch, setSelectedBranch])
  useEffect(() => {
    if (targetSelection.kind !== 'repository' || !onRepositorySelectionChange) return
    onRepositorySelectionChange({
      nameWithOwner: targetSelection.repository.nameWithOwner,
      branch: selectedBranch || targetSelection.repository.defaultBranch,
    })
  }, [onRepositorySelectionChange, selectedBranch, targetSelection])
  // The scope stays here across Sandbox Chat and repository tasks. A launched
  // repository seeds the owner preference rather than restricting destinations.
  const restoredEnvironmentKindOnMount = mountInitialInput?.restoreLastEnvironment
    ? resolvePersistedEnvironmentOption({
        supportsManagedSandbox: supportsSelectedRepositorySandbox || isRepositorySandboxPolicyPending,
        hasEnvironmentSubmit: Boolean(host.createEnvironmentTask),
      })?.kind
    : undefined
  const initialEnvironmentIsSandbox =
    mountInitialInput?.initialEnvironment === 'sandbox' ||
    requiresSandboxTaskEnvironment ||
    (Boolean(mountInitialInput?.restoreLastEnvironment) && restoredEnvironmentKindOnMount !== 'cloud')
  const initialTaskScopeOrganization = initialEnvironmentIsSandbox ? getInitialRepositoryOwner(initialTask) : undefined
  const nwoParamOwner = initialTarget ? undefined : (searchParams.get('repo')?.split('/', 1)[0] ?? undefined)
  const initialChatScopeOwner =
    initialChatScope.scope?.kind === 'organization'
      ? initialChatScope.scope.organization.login
      : initialChatScope.preservedOwner
  const initialScopeLogin = initialTaskScopeOrganization ?? initialChatScopeOwner ?? nwoParamOwner
  const scope = useTaskComposerScope(
    availableOrganizations,
    supportsSelectedRepositorySandbox,
    initialScopeLogin,
    currentUserLogin,
    lastContributedOrganizationLogin,
    copilotFeatureFlags.oneCopilotChatOwnerSplitPicker,
  )
  const {selectedScope, setSelectedScope, setDerivedSelectedScope, pendingSsoOrgLogin} = scope
  const restoredSsoScopeOwner =
    pendingSsoOrgLogin &&
    selectedScope.kind === 'organization' &&
    selectedScope.organization.login.toLowerCase() === pendingSsoOrgLogin.toLowerCase() &&
    !selectedScope.organization.requiresSso
      ? selectedScope.organization.login
      : undefined
  const [preservedChatScopeOwner, setPreservedChatScopeOwner] = useState(
    restoredSsoScopeOwner ? undefined : initialChatScope.preservedOwner,
  )
  const chatScopeBeforeRepositoryRef = useRef<{
    selectedScope: TaskComposerScope
    preservedOwner: string | undefined
  }>({selectedScope, preservedOwner: preservedChatScopeOwner})
  useEffect(() => {
    if (targetSelection.kind === 'chat') {
      chatScopeBeforeRepositoryRef.current = {selectedScope, preservedOwner: preservedChatScopeOwner}
    }
  }, [targetSelection.kind, selectedScope, preservedChatScopeOwner])
  const blockedScopeLogin =
    targetSelection.kind === 'chat' &&
    initialChatScope.preservedOwnerRequiresSso &&
    preservedChatScopeOwner?.toLowerCase() === initialChatScope.preservedOwner?.toLowerCase()
      ? preservedChatScopeOwner
      : undefined
  const handleScopeSelection = useCallback<Dispatch<SetStateAction<TaskComposerScope>>>(
    selection => {
      setPreservedChatScopeOwner(undefined)
      setSelectedScope(selection)
    },
    [setPreservedChatScopeOwner, setSelectedScope],
  )
  const handleDerivedScopeSelection = useCallback<Dispatch<SetStateAction<TaskComposerScope>>>(
    selection => {
      setPreservedChatScopeOwner(undefined)
      setDerivedSelectedScope(selection)
    },
    [setDerivedSelectedScope, setPreservedChatScopeOwner],
  )
  const composerScope = useMemo(
    () => ({
      selectedScope,
      setSelectedScope: handleScopeSelection,
      setDerivedSelectedScope: handleDerivedScopeSelection,
      pendingSsoOrgLogin,
      ...(targetSelection.kind === 'repository'
        ? {launchScopeLogin: targetSelection.repository.owner}
        : preservedChatScopeOwner
          ? {launchScopeLogin: preservedChatScopeOwner}
          : {}),
      ...(blockedScopeLogin ? {blockedScopeLogin} : {}),
    }),
    [
      blockedScopeLogin,
      handleDerivedScopeSelection,
      handleScopeSelection,
      pendingSsoOrgLogin,
      preservedChatScopeOwner,
      selectedScope,
      targetSelection,
    ],
  )
  // A handoff records the turn for a destination that provisions it later, so it
  // is exempt from the environments rollout that binding an environment needs —
  // but not from Sandbox itself, which is still where Chat lives.
  const isChatHandoff = chatLaunch?.kind === 'handoff'
  // Pending is not denied; keep Sandbox selected until policy resolves.
  const sandboxDisallowedByPolicy =
    targetSelection.kind === 'repository'
      ? selectedRepositorySandboxEnabled === false
      : supportsManagedSandbox === false
  const deliversSandboxByHandoff = isChatHandoff && !sandboxDisallowedByPolicy
  const offersEnvironmentsByHandoff =
    isChatHandoff && !chatLaunch.submitRepositoryAsChat && hasTaskDestination && Boolean(host.createEnvironmentTask)
  const submitsSandboxByHandoff = offersEnvironmentsByHandoff && deliversSandboxByHandoff
  // Chat, SSO, and an explicit `initialEnvironment: 'sandbox'` hard-force
  // Sandbox. A `restoreLastEnvironment` launch (e.g. the sidebar's "New
  // session" button) opts into restoring the user's last-selected environment,
  // falling back to Sandbox for a first-time entry.
  const forcesSandbox =
    mountInitialInput?.initialEnvironment === 'sandbox' ||
    requiresSandboxTaskEnvironment ||
    initialTarget?.kind === 'chat' ||
    Boolean(pendingSsoOrgLogin)
  const persistedEnvironment =
    forcesSandbox || !mountInitialInput?.restoreLastEnvironment
      ? null
      : resolvePersistedEnvironmentOption({
          supportsManagedSandbox: supportsSelectedRepositorySandbox || isRepositorySandboxPolicyPending,
          hasEnvironmentSubmit: Boolean(host.createEnvironmentTask),
          deliversByHandoff: deliversSandboxByHandoff,
        })
  const hasPersistedEnvironment = Boolean(copilotLocalStorage.getLastSelectedEnvironmentId())
  const simplifiedEnvironmentPicker = isFeatureEnabled('one_copilot_simplified_environment_picker')
  const environment = useEnvironmentSelection({
    onEnvironmentSubmit: host.createEnvironmentTask,
    supportsManagedSandbox: supportsSelectedRepositorySandbox,
    deliversByHandoff: deliversSandboxByHandoff,
    hydratePersistedAhp: !simplifiedEnvironmentPicker,
    sandboxPolicyPending: isRepositorySandboxPolicyPending,
    initialEnvironment: forcesSandbox
      ? SANDBOX_ENVIRONMENT_OPTION
      : mountInitialInput?.restoreLastEnvironment
        ? (persistedEnvironment ?? (hasPersistedEnvironment ? null : SANDBOX_ENVIRONMENT_OPTION))
        : null,
  })
  const {selectedEnvironment, setSelectedEnvironment, isSandboxEnvironment, showSandboxOption, showEnvironmentPicker} =
    environment

  const hasImplicitLauncherAvailability = chatLaunch?.kind === 'environment' && chatLaunch.availability === 'implicit'
  const bypassesEnvironmentSelection = isChatHandoff || hasImplicitLauncherAvailability
  const controllerEnvironment = useMemo(() => {
    // An implicit launcher cannot force a policy-denied Sandbox selection.
    if (hasImplicitLauncherAvailability && !environment.selectedEnvironment && !sandboxDisallowedByPolicy) {
      return {
        ...environment,
        selectedEnvironment: SANDBOX_ENVIRONMENT_OPTION,
        isAhpEnvironment: true,
        isSandboxEnvironment: true,
      }
    }
    if (
      submitsSandboxByHandoff &&
      targetSelection.kind === 'repository' &&
      environment.selectedEnvironment?.kind === 'sandbox'
    ) {
      return {...environment, isAhpEnvironment: true, isSandboxEnvironment: true}
    }
    return environment
  }, [
    environment,
    hasImplicitLauncherAvailability,
    sandboxDisallowedByPolicy,
    submitsSandboxByHandoff,
    targetSelection.kind,
  ])
  // An in-place handoff host provisions Sandbox itself rather than binding an
  // environment, so its built-in destinations stay visible regardless of the
  // environments rollout. Navigating handoffs and other task hosts keep the
  // default picker behavior.
  // A host that starts only Sandbox sessions is offered only Sandbox, and is
  // shown the picker regardless of the environments rollout: the rollout gates
  // *binding* an environment for a task, which such a host never does.
  const environmentOptions = requiresSandboxTaskEnvironment
    ? supportsSelectedRepositorySandbox || isRepositorySandboxPolicyPending
      ? SANDBOX_ONLY_ENVIRONMENT_OPTIONS
      : undefined
    : submitsSandboxByHandoff
      ? [CLOUD_ENVIRONMENT_OPTION, SANDBOX_ENVIRONMENT_OPTION]
      : offersEnvironmentsByHandoff
        ? [CLOUD_ENVIRONMENT_OPTION]
        : resolveEnvironmentOptions({hasTaskDestination})
  const showEnvironmentControl = showEnvironmentPicker || Boolean(environmentOptions)
  const isSandboxDraft = isSandboxComposedDraft({
    isSandboxEnvironment,
    selectedEnvironmentKind: selectedEnvironment?.kind,
    deliversByHandoff: bypassesEnvironmentSelection,
  })
  const showsImplicitSandboxScope =
    hasImplicitLauncherAvailability &&
    controllerEnvironment.isSandboxEnvironment &&
    !isSandboxDraft &&
    Boolean(preservedChatScopeOwner)
  // Availability only: the task controller no longer carries a Chat send of its
  // own, so declaring the launcher cannot race the send this layer owns.
  const launchScopeLogin = preservedChatScopeOwner ?? scopeLogin(selectedScope, currentUserLogin)
  const hasSpecifiedTaskRepository = hasSpecifiedRepository && targetSelection.kind !== 'chat'
  // Chat requires Sandbox even when launch bypasses environment selection.
  const chatDisallowedByPolicy = (isChatHandoff || hasImplicitLauncherAvailability) && sandboxDisallowedByPolicy
  const disallowedChatSelected = chatDisallowedByPolicy && targetSelection.kind === 'chat'
  const sandboxRequiredButUnavailable = requiresSandboxTaskEnvironment && sandboxDisallowedByPolicy
  const canPrepareChat =
    !chatDisallowedByPolicy &&
    canPrepareTaskComposerChat({
      chatLaunch: hasImplicitLauncherAvailability ? {kind: 'handoff'} : chatLaunch,
      hasEnvironmentSubmit: Boolean(host.createEnvironmentTask),
      hideRepoSelector,
      showSandboxOption,
      hasSpecifiedRepository: hasSpecifiedTaskRepository,
      scopeLogin: launchScopeLogin,
    })
  const canSelectChat =
    !chatDisallowedByPolicy &&
    canPrepareTaskComposerChat({
      chatLaunch,
      hasEnvironmentSubmit: Boolean(host.createEnvironmentTask),
      hideRepoSelector,
      showSandboxOption,
      hasSpecifiedRepository: false,
      scopeLogin: launchScopeLogin,
    })
  useEffect(() => {
    if (pendingSsoOrgForChat === null || handledPendingSsoChatTarget.current || pendingSsoChatFrame.current !== null) {
      return
    }
    pendingSsoChatFrame.current = requestAnimationFrame(() => {
      pendingSsoChatFrame.current = null
      if (availableOrganizations.length === 0) {
        setRestoredSsoOrgForChat(pendingSsoOrgForChat)
        return
      }
      handledPendingSsoChatTarget.current = true
      if (pendingSsoChatTarget) {
        setRestoredSsoOrgForChat(pendingSsoOrgForChat)
        const authorizedOrg = availableOrganizations.find(
          org =>
            org.login.toLowerCase() === pendingSsoOrgForChat.toLowerCase() &&
            org.requiresSso !== true &&
            isOrganizationSandboxEnabled(org),
        )
        if (
          authorizedOrg &&
          (selectedScope.kind !== 'organization' ||
            selectedScope.organization.login.toLowerCase() !== authorizedOrg.login.toLowerCase())
        ) {
          setDerivedSelectedScope({kind: 'organization', organization: authorizedOrg})
        }
        setTargetSelection(authorizedOrg && canSelectChat ? {kind: 'chat'} : {kind: 'unselected'})
        setSelectedBranch('')
        onRepositorySelectionChange?.(null)
      }
      clearPendingSsoChatTarget()
    })
  }, [
    availableOrganizations,
    canSelectChat,
    onRepositorySelectionChange,
    pendingSsoChatTarget,
    pendingSsoOrgForChat,
    selectedScope,
    setDerivedSelectedScope,
    setSelectedBranch,
    setTargetSelection,
  ])
  const hasOwnerChoice = availableOrganizations.length > 0 || Boolean(preservedChatScopeOwner)
  const ownerSplitPickerActive = isOwnerSplitPickerActive({
    flagEnabled: copilotFeatureFlags.oneCopilotChatOwnerSplitPicker,
    isSandboxDraft,
    selectedEnvironmentKind: selectedEnvironment?.kind,
    canSelectChat,
    currentUserLogin,
    hasOwnerChoice,
    hasOwnerPanel: Boolean(scopeOwnerPanel),
  })
  // Chat ranks the selected owner's repositories first without limiting the
  // destination. Repository tasks derive their owner from the destination.
  const preferredRepositoryOwner = isSandboxDraft && targetSelection.kind === 'chat' ? launchScopeLogin : undefined
  const sandboxDisabledOwnersKey = preferredRepositoryOwner
    ? availableOrganizations
        .filter(org => org.sandboxEnabled === false)
        .map(org => org.login.toLowerCase())
        .sort()
        .join(',')
    : ''
  const sandboxDisabledOwners = useMemo(
    () => (sandboxDisabledOwnersKey ? sandboxDisabledOwnersKey.split(',') : undefined),
    [sandboxDisabledOwnersKey],
  )

  // A repository's owner is inferred from the destination rather than picked in
  // the ScopePicker, which is hidden for repository targets. Recording it as an
  // explicit choice would permanently suppress the server default.
  const selectRepositoryOwnerScope = useCallback(
    (owner: string | undefined) => {
      if (!owner) return
      if (owner.toLowerCase() === currentUserLogin?.toLowerCase()) {
        handleDerivedScopeSelection({kind: 'personal'})
        return
      }
      const organization = availableOrganizations.find(
        candidate => candidate.login.toLowerCase() === owner.toLowerCase(),
      )
      if (organization && isOrganizationSandboxEnabled(organization)) {
        handleDerivedScopeSelection({kind: 'organization', organization})
      } else {
        // A denied owner would make the nested task composer clear its repository.
        setPreservedChatScopeOwner(organization ? currentUserLogin : owner)
      }
    },
    [availableOrganizations, currentUserLogin, handleDerivedScopeSelection, setPreservedChatScopeOwner],
  )

  const handleTargetSelection = useCallback(
    (selection: TaskComposerTargetSelection, restoreChatScope = true) => {
      if (selection.kind === 'chat' && targetSelection.kind !== 'chat' && restoreChatScope) {
        const previousChatScope = chatScopeBeforeRepositoryRef.current
        handleDerivedScopeSelection(previousChatScope.selectedScope)
        setPreservedChatScopeOwner(previousChatScope.preservedOwner)
      }
      setTargetSelection(selection)
      if (selection.kind === 'repository') {
        selectRepositoryOwnerScope(selection.repository.owner)
      } else {
        setSelectedBranch('')
        onRepositorySelectionChange?.(null)
      }
    },
    [
      onRepositorySelectionChange,
      handleDerivedScopeSelection,
      selectRepositoryOwnerScope,
      setSelectedBranch,
      setTargetSelection,
      targetSelection.kind,
    ],
  )
  const handleEnvironmentSelect = useCallback(
    (nextEnvironment: EnvironmentOption) => {
      if (readOnly) return
      if (nextEnvironment.kind === 'sandbox' && targetSelection.kind === 'repository') {
        selectRepositoryOwnerScope(targetSelection.repository.owner)
      }
      setSelectedEnvironment(nextEnvironment)
      // Sandbox is where Chat lives, but an explicit repository remains the
      // destination when moving a task between Actions and Sandbox.
      if (nextEnvironment.kind === 'sandbox' && canPrepareChat && targetSelection.kind !== 'repository') {
        handleTargetSelection({kind: 'chat'})
      } else if (targetSelection.kind === 'chat') {
        // Chat cannot be started outside Sandbox, so leaving it must take the
        // destination with it rather than stranding a Chat draft on Actions.
        // The simplified picker keeps Actions inactive during Chat, so this
        // guards the default picker and non-picker selection changes.
        handleTargetSelection({kind: 'unselected'})
        // Only reachable from Chat, which never holds a repository, so the
        // announcement names what the user now has to do rather than claiming a
        // selection was cleared.
        announce('Chat is only available in Sandbox. Choose a repository to create a task.', {
          assertive: false,
          element: textAreaRef.current ?? undefined,
        })
      }
    },
    [
      canPrepareChat,
      handleTargetSelection,
      readOnly,
      selectRepositoryOwnerScope,
      setSelectedEnvironment,
      targetSelection,
      textAreaRef,
    ],
  )

  const [isSubmitting, setIsSubmitting] = useState(false)
  const [hasSubmitted, setHasSubmitted] = useState(false)
  const tourEnabled = showEnvironmentStripTour && isEnvironmentStripTourEnabled()
  const isTourShowingRef = useRef(false)
  const onTourVisibilityChange = useCallback((visible: boolean) => {
    isTourShowingRef.current = visible
  }, [])
  const onBeforeSubmit = useCallback(() => {
    if (isTourShowingRef.current) {
      isTourShowingRef.current = false
      void dismissNotice(ONE_COPILOT_ENVIRONMENT_STRIP_TOUR_NOTICE)
    }
    setHasSubmitted(true)
  }, [])

  const containerClassName = clsx(
    styles.composerMinHeight,
    isNewConversation && styles.composerNewConversationMinHeight,
    // Establishes the container the toolbar pickers compact against, so a
    // narrow host (the embedded panel is ~400px) compacts them before a
    // session exists exactly as the established composer does after.
    responsiveStyles.container,
  )
  const initialSubmission =
    mountInitialInput?.behavior === 'submit'
      ? {
          id: mountInitialInput.id,
          prompt: mountInitialInput.prompt.text,
          directTurnTelemetrySeed: mountInitialInput.directTurnTelemetrySeed,
        }
      : undefined
  const [scopePickerFocusRequest, setScopePickerFocusRequest] = useState(0)
  const [chatPickerReopenRequested, setChatPickerReopenRequested] = useState(false)
  const handleChatPickerReopenHandled = useCallback(() => setChatPickerReopenRequested(false), [])
  useEffect(() => {
    if (scopePickerFocusRequest > 0) {
      const textArea = textAreaRef.current
      // Let the menu's trap and the selecting key's native default action finish.
      const frame = requestAnimationFrame(() => {
        if (
          textArea?.isConnected &&
          textAreaRef.current === textArea &&
          textArea.ownerDocument.activeElement === textArea.ownerDocument.body
        ) {
          textArea.focus()
        }
      })
      return () => cancelAnimationFrame(frame)
    }
  }, [scopePickerFocusRequest, textAreaRef])
  const handleScopePickerSelection = (selection: SetStateAction<TaskComposerScope>) => {
    handleScopeSelection(selection)
    if (availableOrganizations.length === 0 || !isSandboxDraft) {
      setScopePickerFocusRequest(request => request + 1)
    }
  }
  const scopeControl =
    scopePicker &&
    !ownerSplitPickerActive &&
    targetSelection.kind === 'chat' &&
    (isSandboxDraft || showsImplicitSandboxScope) &&
    currentUserLogin &&
    (availableOrganizations.length > 0 || Boolean(preservedChatScopeOwner))
      ? scopePicker({
          selectedScope,
          onSelectScope: handleScopePickerSelection,
          retainedOwnerLogin: preservedChatScopeOwner,
          organizations: availableOrganizations,
          currentUserLogin,
          currentUserAvatarUrl,
          disabled: readOnly || isSubmitting,
        })
      : undefined
  const chatOwner: ChatOwnerControl | undefined =
    ownerSplitPickerActive && currentUserLogin
      ? {
          ...buildChatOwnerPresentation({
            selectedScope,
            retainedOwnerLogin: preservedChatScopeOwner,
            currentUserLogin,
            currentUserAvatarUrl,
          }),
          reopenRequested: chatPickerReopenRequested,
          onReopenHandled: handleChatPickerReopenHandled,
          renderPanel:
            scopeOwnerPanel && hasOwnerChoice
              ? () =>
                  scopeOwnerPanel({
                    selectedScope,
                    onSelectScope: selection => {
                      handleScopeSelection(selection)
                      handleTargetSelection({kind: 'chat'}, false)
                      setChatPickerReopenRequested(true)
                    },
                    retainedOwnerLogin: preservedChatScopeOwner,
                    organizations: availableOrganizations,
                    currentUserLogin,
                    currentUserAvatarUrl,
                    disabled: readOnly || isSubmitting,
                    onBeforeSsoNavigate: () => {
                      setTargetMarkerWrittenHere(true)
                      setPendingSsoChatTarget()
                    },
                  })
              : undefined,
        }
      : undefined
  // A strip with no control left to offer is an empty band above the composer,
  // so it is withheld rather than drawn. A locked repository still offers its
  // branch there.
  const showEnvironmentStrip = showEnvironmentControl || hasRepositoryDestination || Boolean(scopeControl)
  const environmentStrip = showEnvironmentStrip ? (
    <EnvironmentStrip
      hideRepoSelector={hideRepoSelector}
      initialRepoNWO={pendingSsoChatTarget ? undefined : initialRepositoryName}
      suppressAutoSelect={pendingSsoChatTarget}
      initialBranch={initialBranch}
      targetSelection={targetSelection}
      onTargetSelectionChange={handleTargetSelection}
      selectedBranch={selectedBranch}
      onBranchSelect={(nextBranch: string | undefined) => setSelectedBranch(nextBranch || '')}
      currentPrompt={currentPrompt}
      currentUserLogin={currentUserLogin}
      selectedEnvironment={selectedEnvironment}
      selectedOption={simplifiedEnvironmentPicker ? controllerEnvironment.selectedEnvironment : undefined}
      design={simplifiedEnvironmentPicker ? 'simplified' : 'default'}
      onSelectEnvironment={handleEnvironmentSelect}
      includeSandboxOption={showSandboxOption}
      allowChat={canSelectChat}
      showEnvironmentPicker={showEnvironmentControl}
      {...(environmentOptions ? {environmentOptions} : {})}
      disabled={readOnly || isSubmitting}
      showTour={tourEnabled}
      submitting={isSubmitting || hasSubmitted}
      textAreaRef={textAreaRef}
      onTourVisibilityChange={onTourVisibilityChange}
      preferredRepositoryOwner={preferredRepositoryOwner}
      sandboxDisabledOwners={sandboxDisabledOwners}
      chatOwner={chatOwner}
      // Interactive Sandbox drafts place scope in the strip. An implicit
      // launch whose strip cannot represent Sandbox uses the toolbar.
      scopePicker={isSandboxDraft ? scopeControl : undefined}
    />
  ) : undefined

  return (
    <div className={clsx(styles.inputWrapper, className)}>
      <NewSessionComposerController
        controller={host}
        configuration={{
          currentUserLogin,
          currentUserAvatarUrl,
          availableOrganizations,
          supportsManagedSandbox: supportsSelectedRepositorySandbox,
          agentQuota,
          mcpPermissionGateEnabled,
          disableSandboxSubmission,
          mode,
          onModeChange,
          approvalMode,
          availableApprovalModes,
          onApprovalModeChange,
          modePicker,
          directTurnTelemetrySurface,
          preserveDirectTurnTelemetryForHandoff: configuration.preserveDirectTurnTelemetryForHandoff,
        }}
        targetState={targetState}
        draft={draft}
        branch={branch}
        environment={controllerEnvironment}
        scope={composerScope}
        initialTask={initialTask}
        initialPreparedPrompt={mountInitialInput?.prompt}
        initialChatProvisioningTarget={initialChatProvisioningTarget}
        initialSpace={
          initialChatProvisioningTarget?.kind === 'space'
            ? initialChatProvisioningTarget.space
            : initialTarget?.kind === 'chat' && initialTarget.context?.kind === 'space'
              ? initialTarget.context.space
              : undefined
        }
        initialBranch={initialBranch}
        initialTarget={initialTarget?.kind}
        repositoryLocked={isRepositoryLocked}
        suppressUrlRepository={pendingSsoChatTarget}
        initialSubmission={initialSubmission}
        scopeControl={showsImplicitSandboxScope ? scopeControl : undefined}
        draftSource={draftSource}
        forceDisabled={readOnly || disallowedChatSelected || sandboxRequiredButUnavailable}
        readOnly={readOnly}
        emptyState={emptyState}
        containerClassName={containerClassName}
        disableWhileSubmitting={disableWhileSubmitting}
        submitWithModifier={submitWithModifier}
        clearOnSubmit={shouldClearTextOnSubmit}
        maxTextareaHeight={maxTextareaHeight}
        handleRef={handleRef}
        disableAutoFocus={disableAutoFocus}
        onSubmittingChange={setIsSubmitting}
        onBeforeSubmit={tourEnabled ? onBeforeSubmit : undefined}
        belowComposerContent={environmentStrip}
      />
    </div>
  )
}

NewSessionComposer.displayName = 'NewSessionComposer'
